import {FileToUpload} from "../models/fileToUpload";
import {
    UploadOperationBase,
    UploadOperationConfig,
    UploadOperationEvents,
    UploadOperationResult, UploadOperationStrings
} from "./uploadOperationBase";
import {apiMultiPartFile} from "../../../../models/apiS3MultiPartFileData";
import {ILogsContainer} from "../../../../logsContainer";
import {UserLogsService} from "../../../../services/userLogs";

/**
 * An interface for declaring labels/static texts associated with the s3 uploader
 * This is useful when we want to localize labels
 */
export interface UploadToS3Strings extends UploadOperationStrings{
    CreatingFileMetaData: string
    CreatingFileSignature: string
    SignatureCouldNotBeGenerated: string
    StartingUpload: string
    UploadingNOfMParts: (uploaded: number, total: number) => string
}

/**
 * Result class for S3 uploader
 * It contains the data returned by the S3 uploader
 */
export interface UploadToS3Result extends UploadOperationResult {

    /**
     * The id of the associated user file in the database
     */
    FileId: string;

    /**
     * The complete S3 url of the file uploaded
     */
    FileUrl: string;
}

/**
 * This interface defines the types of events the S3 uploader supports
 */
export interface UploadToS3Events extends UploadOperationEvents<UploadToS3Result> {
    /**
     * This event is raised after the chunk of a mutlipart upload operation is uploaded
     * @param fileId The uploaded chunk
     * @constructor
     */
    OnFileCunkUploaded?: (chunk: apiMultiPartFile) => void;
}

/**
 * Configuration class for S3 uploader
 */
export class UploadToS3Config extends UploadOperationConfig<UploadToS3Result, UploadToS3Strings, UploadToS3Events> {
    /**
     * The bucket we want to upload to
     */
    public readonly Bucket: string;

    /**
     * The region the bucket belongs to
     */
    public readonly Region: string;

    /**
     * The relative path on the S3 bucket where you want to file to be placed - For example: 'userfiles/'
     */
    public readonly Folder: string;

    /**
     * The url of the request which creates the initial user-file in the db and gets the signed link
     * This is used for single-part file uploads
     */
    public readonly SingleFileStartUploadUrl: string;

    /**
     * The url of the request which creates the initial user-file in the db and also initiates the multipart upload on AWS
     * This is used for multipart file uploads
     */
    public readonly MultiPartFileStartUploadUrl: string;

    /**
     * The url of the request which marks the completion of the multipart upload on AWS
     * This is used for multipart file uploads
     */
    public readonly MultiPartFileFinishUploadUrl: string;
    
    /**
     * Constructs the config
     * @param bucket The bucket we want to upload to
     * @param region The region the bucket belongs to
     * @param folder The relative path on the S3 bucket where you want to file to be placed - For example: 'userfiles/'
     * @param singleFileStartUploadUrl The url of the request which creates the initial user-file in the db and gets the signed link
     * This is used for single-part file uploads
     * @param multiPartFileStartUploadUrl The url of the request which creates the initial user-file in the db and also initiates the multipart upload on AWS
     * This is used for multipart file uploads
     * @param multiPartFileFinishUploadUrl The url of the request which marks the completion of the multipart upload on AWS
     * This is used for multipart file uploads 
     * @param allowMultipleFiles True if multiple files can be uploaded; False otherwise
     * @param allowedMaxSizeInBytes Maximum file size allowed (in bytes) - Defaults to any size
     * @param allowedFileExtensions The file extensions we allow - Blank array means all extensions allowed
     * @param authToken The authorization-token if the user file creation endpoint is a protected endpoint
     * @param events The events supported by the S3 upload operation
     * @param allowVideoRecording True if video recording from webcam is allowed; False otherwise
     * @param allowAudioRecording True if audio recording is allowed; False otherwise
     * @param allowScreenRecording True if screen recording is allowed; False otherwise
     * @param allowScreenAndVideoRecording True if the combined screen and video recording is allowed; False otherwise
     * @param allowPickedFiles True if pick file option is allowed
     * @param allowMicrophone True if microphone is allowed to be recorded
     * @param staticStrings Any labels/static texts associated with the component
     */
    constructor(bucket: string, 
                region: string,
                folder: string,
                singleFileStartUploadUrl: string,
                multiPartFileStartUploadUrl: string,
                multiPartFileFinishUploadUrl: string,
                allowMultipleFiles: boolean = false, 
                allowedMaxSizeInBytes: number | null = null, 
                allowedFileExtensions: Array<string> = [],
                authToken: string = "",
                events: UploadToS3Events | null = null,
                allowVideoRecording: boolean = false,
                allowAudioRecording: boolean = false,
                allowScreenRecording: boolean = false,
                allowScreenAndVideoRecording: boolean = false,
                allowPickedFiles: boolean = false,
                allowMicrophone: boolean = false,
                staticStrings: UploadToS3Strings | null = null) {
        
        super(allowMultipleFiles, allowedMaxSizeInBytes, allowedFileExtensions, authToken, events, allowVideoRecording, allowAudioRecording, allowScreenRecording, allowScreenAndVideoRecording, allowPickedFiles, allowMicrophone, staticStrings);
        
        this.StaticStrings.CreatingFileMetaData = staticStrings?.CreatingFileMetaData ?? labels.CreatingFileMetaData ?? "Creating file meta data..";
        this.StaticStrings.CreatingFileSignature = staticStrings?.CreatingFileSignature ?? labels.CreatingFileSignature ?? "Creating file signature..";
        this.StaticStrings.SignatureCouldNotBeGenerated = staticStrings?.SignatureCouldNotBeGenerated ?? labels.SignatureCouldNotBeGenerated ?? "Signature couldn't be generated..";
        this.StaticStrings.StartingUpload = staticStrings?.StartingUpload ?? labels.StartingUpload ?? "Starting upload..";
        this.StaticStrings.UploadingNOfMParts = staticStrings?.UploadingNOfMParts != null ?
            staticStrings.UploadingNOfMParts :
            (uploaded, total) => labels.UploadingNOfMParts?.replace('{0}', uploaded.toString()).replace('{1}', total.toString()) ?? `Uploading ${uploaded}/${total} part(s)`;
        
        this.Bucket = bucket;
        this.Region = region;
        this.Folder = folder;
        this.SingleFileStartUploadUrl = singleFileStartUploadUrl;
        this.MultiPartFileStartUploadUrl = multiPartFileStartUploadUrl;
        this.MultiPartFileFinishUploadUrl = multiPartFileFinishUploadUrl;
    }
}

/**
 * The base class for all S3 uploaders
 */
export abstract class UploadToS3 extends UploadOperationBase<UploadToS3Result, UploadToS3Strings, UploadToS3Events, UploadToS3Config> {
    /**
     * Initialises the S3 upload operation
     * @param file The file to upload
     * @param config The S3 upload operation config
     * @param logs The logs container
     * @param userLogsService The user logs service to send logs to the server
     */
    protected constructor(file: FileToUpload, config: UploadToS3Config, logs: ILogsContainer, userLogsService: UserLogsService) {
        super(file, config, logs, userLogsService);
    }
}